import { createSessionScope } from '@scrambl/shared/session-scope'

interface CredentialSnapshot {
  revision: string | null
  sessionRevision?: string | null
  data: Record<string, unknown>
  pendingAccountRemovals?: NativeAccountRemoval[]
}

export interface NativeAccountRemoval {
  readonly operationId: string
  readonly accountId: string
  readonly loginFamilyId: string | null
  readonly ownerId: string | null
  readonly completed: boolean
}
export type NativeAccountRemovalRequest = Pick<
  NativeAccountRemoval,
  'operationId' | 'accountId' | 'loginFamilyId'
>

export interface CredentialTransport {
  read(): Promise<CredentialSnapshot>
  write(
    expectedRevision: string | null,
    data: Record<string, unknown>,
  ): Promise<CredentialSnapshot>
  beginAccountRemoval?(
    expectedRevision: string | null,
    request: NativeAccountRemovalRequest,
  ): Promise<{ snapshot: CredentialSnapshot; removal: NativeAccountRemoval }>
  completeAccountRemoval?(operationId: string): Promise<CredentialSnapshot>
}

// Publish reads and committed writes in order, including native event reads.
// Otherwise an older read can finish after a fresh login and restore its epoch.
let credentialOperations: Promise<unknown> = Promise.resolve()
function queueCredentialOperation<T>(operation: () => Promise<T>): Promise<T> {
  const next = credentialOperations.catch(() => undefined).then(operation)
  credentialOperations = next
  return next
}

function applySession(snapshot: CredentialSnapshot): void {
  const identity =
    typeof snapshot.data.auth_active_account_id === 'string'
      ? snapshot.data.auth_active_account_id
      : null
  const accounts = snapshot.data.auth_accounts as
    | Record<string, { loginFamilyId?: unknown }>
    | undefined
  const family = identity ? accounts?.[identity]?.loginFamilyId : null
  desktopAccountSession.transition(
    identity,
    identity
      ? JSON.stringify([
          typeof family === 'string' &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            family,
          )
            ? family
            : null,
          snapshot.sessionRevision ?? null,
        ])
      : (snapshot.sessionRevision ?? null),
  )
}

/** Store-compatible adapter backed exclusively by the native OS credential vault. */
export class NativeCredentialStore {
  private snapshot: CredentialSnapshot = { revision: null, data: {} }
  constructor(private readonly transport: CredentialTransport) {}

  async reload(): Promise<void> {
    await queueCredentialOperation(async () => {
      this.snapshot = await this.transport.read()
      applySession(this.snapshot)
    })
  }
  async get<T>(key: string): Promise<T | undefined> {
    return structuredClone(this.snapshot.data[key]) as T | undefined
  }
  async set(key: string, value: unknown): Promise<void> {
    this.snapshot.data[key] = structuredClone(value)
  }
  async delete(key: string): Promise<boolean> {
    const existed = key in this.snapshot.data
    delete this.snapshot.data[key]
    return existed
  }
  async save(): Promise<void> {
    // Native compare-and-swap refuses an old window's stale account snapshot.
    const revision = this.snapshot.revision
    const data = structuredClone(this.snapshot.data)
    await queueCredentialOperation(async () => {
      this.snapshot = await this.transport.write(revision, data)
      applySession(this.snapshot)
    })
  }
  getPendingAccountRemovals(): NativeAccountRemoval[] {
    if (!this.snapshot.pendingAccountRemovals)
      throw new Error('Update the desktop app before removing account data.')
    return structuredClone(this.snapshot.pendingAccountRemovals)
  }
  async beginAccountRemoval(
    request: NativeAccountRemovalRequest,
  ): Promise<NativeAccountRemoval> {
    // Capture the caller's original selection, rather than borrowing a revision
    // observed by some later operation while this request waits in the queue.
    const revision = this.snapshot.revision
    const captured = structuredClone(request)
    return queueCredentialOperation(async () => {
      if (!this.transport.beginAccountRemoval)
        throw new Error('Update the desktop app before removing account data.')
      const result = await this.transport.beginAccountRemoval(
        revision,
        captured,
      )
      this.snapshot = result.snapshot
      applySession(this.snapshot)
      return structuredClone(result.removal)
    })
  }
  /** Call only after every cleanup stage acknowledges the immutable operation. */
  async completeAccountRemoval(operationId: string): Promise<void> {
    await queueCredentialOperation(async () => {
      if (!this.transport.completeAccountRemoval)
        throw new Error('Update the desktop app before removing account data.')
      this.snapshot = await this.transport.completeAccountRemoval(operationId)
      applySession(this.snapshot)
    })
  }
}

export async function loadNativeCredentialStore(): Promise<NativeCredentialStore> {
  const { invoke } = await import('@tauri-apps/api/core')
  accountListenerReady ??= import('@tauri-apps/api/event')
    .then(async ({ listen }) => {
      await listen('desktop-session-changed', () => {
        void queueCredentialOperation(async () => {
          applySession(
            await invoke<CredentialSnapshot>('credential_store_read'),
          )
        }).catch(() => {
          // An unavailable vault cannot establish a new owner. Fence pending
          // work until a successful read or explicit sign-in recovers it.
          desktopAccountSession.transition(null)
        })
      })
    })
    .catch(error => {
      accountListenerReady = undefined
      throw error
    })
  await accountListenerReady
  const store = new NativeCredentialStore({
    read: () => invoke('credential_store_read'),
    write: (expectedRevision, data) =>
      invoke('credential_store_write', { expectedRevision, data }),
    beginAccountRemoval: (expectedRevision, request) =>
      invoke('credential_account_removal_begin', {
        expectedRevision,
        ...request,
      }),
    completeAccountRemoval: operationId =>
      invoke('credential_account_removal_complete', { operationId }),
  })
  await store.reload()
  return store
}

/** Keep the public account ID while binding work to its native login/selection epoch. */
export function createDesktopAccountSession() {
  const scope = createSessionScope()
  let accountId: string | null = null
  return {
    transition(
      nextAccountId: string | null,
      nativeSession: string | null = null,
    ) {
      accountId = nextAccountId
      scope.transition(
        nextAccountId !== null || nativeSession !== null
          ? JSON.stringify([nextAccountId, nativeSession])
          : null,
      )
    },
    subscribe: scope.subscribe,
    capture() {
      const captured = scope.capture()
      return {
        ...captured,
        identity: accountId,
        credentialSessionKey: captured.identity,
      }
    },
  }
}

export const desktopAccountSession = createDesktopAccountSession()
let accountListenerReady: Promise<void> | undefined
